import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../lib/siteData";
import Logo from "./Logo";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className="fixed top-0 inset-x-0 z-50 bg-[#FCFCFA]/95 backdrop-blur-md border-b border-[#E5E5DF]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <Logo size={42} variant="dark" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9" aria-label="Primary">
          {NAV_LINKS.map((l) =>
            l.external ? (
              <a
                key={l.to}
                href={l.to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={l.testid}
                className="relative text-[12px] xl:text-[13px] uppercase tracking-[0.18em] font-medium text-[#5C636A] hover:text-[#1A1D20] transition-colors duration-200"
              >
                <span className="relative">
                  {l.label}
                  <span className="absolute -bottom-2 left-0 h-[1px] bg-[#D4AF37] w-0 hover:w-full transition-all duration-300" />
                </span>
              </a>
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `relative text-[12px] xl:text-[13px] uppercase tracking-[0.18em] font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-[#1A1D20]"
                      : "text-[#5C636A] hover:text-[#1A1D20]"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="relative">
                    {l.label}
                    <span
                      className={`absolute -bottom-2 left-0 h-[1px] bg-[#D4AF37] transition-all duration-300 ${
                        isActive ? "w-full" : "w-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            )
          )}
        </nav>

        <Link
          to="/investor-relations"
          data-testid="cta-invest"
          className="hidden lg:inline-flex items-center bg-[#1A1D20] text-[#FCFCFA] hover:bg-[#D4AF37] hover:text-[#1A1D20] transition-colors duration-300 px-6 py-3 uppercase tracking-[0.18em] text-[11px] font-semibold"
        >
          Invest With Us
        </Link>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-[#1A1D20]"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="lg:hidden bg-[#FCFCFA] border-t border-[#E5E5DF]"
          data-testid="mobile-menu"
        >
          <nav className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((l) =>
              l.external ? (
                <a
                  key={l.to}
                  href={l.to}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`mobile-${l.testid}`}
                  className="text-sm uppercase tracking-[0.2em] text-[#5C636A]"
                >
                  {l.label}
                </a>
              ) : (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `text-sm uppercase tracking-[0.2em] ${
                      isActive ? "text-[#1A1D20]" : "text-[#5C636A]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              )
            )}
            <Link
              to="/investor-relations"
              data-testid="mobile-cta-invest"
              className="mt-2 bg-[#1A1D20] text-[#FCFCFA] px-6 py-3 uppercase tracking-[0.18em] text-xs text-center"
            >
              Invest With Us
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
